import { NotFoundError } from '@evogard/ui';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  CatalogVehicle,
  Claim,
  ClaimSummary,
  CreateClaim,
  UpdateClaim,
} from '@evogard/contract';
import {
  loadFailureMessage,
  type ClaimDepartment,
  type ClaimType,
} from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';

/**
 * Toda conversa da interna com o sinistro passa por aqui — a lista, a ficha,
 * abrir, corrigir e arquivar.
 *
 * As regras de consulta da casa já vêm dentro: a praça na chave, o resultado
 * anterior segurado só na mesma praça, e toda escrita invalidando a lista e a
 * ficha juntas (arquivar na ficha precisa sumir da lista). O primeiro
 * segmento é `sinistros`, o id do módulo no menu.
 *
 * Esconder não é proteger: `useClaimPermissions` só decide o que a tela
 * oferece. Quem protege é o `PermissionGuard` da API.
 */

const ROOT = ['sinistros', 'sinistro'] as const;

/** Os recortes do filtro "Prazo" — os mesmos da rota (`deadlineCondition`, na API). */
export type ClaimDeadlineFilter =
  | 'sla_expirado'
  | 'sla_expira_em_breve'
  | 'vencido'
  | 'vence_em_breve'
  | 'sem_vencimento';

export function useClaimPermissions() {
  const { data: context } = useSessionContext();
  return {
    canView: can(context, 'claims.notices', 'ver'),
    canCreate: can(context, 'claims.notices', 'criar'),
    canEdit: can(context, 'claims.notices', 'editar'),
    /** A busca de veículo no espelho do SGA — sem ela, o código é digitado. */
    canSearchVehicles: can(context, 'erp.catalog', 'ver'),
  };
}

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

export interface ClaimListFilter {
  search: string;
  currentDepartment?: ClaimDepartment;
  /** Uma fase de `currentDepartment` — só faz sentido junto com ele. */
  currentPhase?: string;
  /** As etapas do supervisor do Reparo: só elas, ou todas menos elas (29/10/2026). */
  supervisionStages?: 'somente' | 'fora';
  type?: ClaimType;
  includeArchived: boolean;
  /**
   * `true`: só os parados (pendência ou pedido de desistência); `false`: só os
   * que não estão parados. Ausente, os dois — as esteiras sempre mandam um.
   */
  onHold?: boolean;
  /** Só os sinistros nesta situação dentro da etapa. */
  situation?: string;
  /** Só os sinistros com este resultado da CAF (16/10/2026). */
  cafStatus?: string;
  /** Só os sinistros que têm esta etiqueta agora. */
  labelId?: number;
  /** O recorte por prazo — SLA expirado, vencidos, sem vencimento (07/10/2026). */
  deadline?: ClaimDeadlineFilter;
  page: number;
  perPage: number;
}

export function useClaimList(filter: ClaimListFilter) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'lista', tenantId, filter],
    queryFn: async () => {
      const r = await api.claims.listClaims({
        query: {
          page: filter.page,
          perPage: filter.perPage,
          ...(filter.search.trim() ? { search: filter.search.trim() } : {}),
          ...(filter.currentDepartment
            ? { currentDepartment: filter.currentDepartment }
            : {}),
          ...(filter.currentPhase ? { currentPhase: filter.currentPhase } : {}),
          ...(filter.supervisionStages
            ? { supervisionStages: filter.supervisionStages }
            : {}),
          ...(filter.type ? { type: filter.type } : {}),
          ...(filter.includeArchived ? { includeArchived: 'true' as const } : {}),
          ...(filter.onHold !== undefined
            ? { onHold: filter.onHold ? 'true' : 'false' }
            : {}),
          ...(filter.situation ? { situation: filter.situation } : {}),
          ...(filter.cafStatus ? { cafStatus: filter.cafStatus } : {}),
          ...(filter.labelId ? { labelId: filter.labelId } : {}),
          ...(filter.deadline ? { deadline: filter.deadline } : {}),
        },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os sinistros'));
      return r.body;
    },
    enabled: tenantId !== undefined,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? keepPreviousData(previous) : undefined,
  });
}

/** O recorte do quadro: o da lista, sem a página — cada coluna pagina sozinha. */
export type ClaimBoardFilter = Omit<
  ClaimListFilter,
  'page' | 'perPage' | 'currentDepartment' | 'currentPhase' | 'supervisionStages'
>;

/**
 * Uma página de UMA fase, para o `loadPage` do quadro. É a mesma rota da lista
 * com `currentPhase`, e não uma consulta do React Query: quem guarda o que já
 * carregou é o `Kanban`, coluna a coluna.
 */
export async function fetchClaimPhasePage(
  department: ClaimDepartment,
  phase: string,
  filter: ClaimBoardFilter,
  page: number,
  perPage: number,
) {
  const r = await api.claims.listClaims({
    query: {
      page,
      perPage,
      currentDepartment: department,
      currentPhase: phase,
      ...(filter.search.trim() ? { search: filter.search.trim() } : {}),
      ...(filter.type ? { type: filter.type } : {}),
      ...(filter.includeArchived ? { includeArchived: 'true' as const } : {}),
      ...(filter.onHold !== undefined
        ? { onHold: filter.onHold ? 'true' : 'false' }
        : {}),
      ...(filter.situation ? { situation: filter.situation } : {}),
      ...(filter.cafStatus ? { cafStatus: filter.cafStatus } : {}),
      ...(filter.labelId ? { labelId: filter.labelId } : {}),
      ...(filter.deadline ? { deadline: filter.deadline } : {}),
    },
  });
  if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os sinistros'));
  return r.body;
}

export function useClaim(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'ficha', tenantId, id],
    queryFn: async () => {
      const r = await api.claims.getClaim({ params: { id: id! } });
      if (r.status === 404)
        throw new NotFoundError('Este sinistro não existe nesta base');
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o sinistro'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/**
 * O veículo do associado no espelho do SGA, pelo código que o sinistro guarda.
 *
 * O sinistro guarda só `vehicle_code` (convenção 10: nada copiado do espelho),
 * e a placa, o modelo e o dono são LIDOS aqui, pela rota do `erp` — regra 1:
 * quem fala com o espelho é o módulo dele. `null` quando o espelho não tem o
 * código, e a tela diz isso em vez de inventar um carro.
 */
export function useErpVehicle(code: string | null) {
  const tenantId = useTenantId();
  const { canSearchVehicles } = useClaimPermissions();
  return useQuery({
    queryKey: ['sinistros', 'veiculo-sga', tenantId, code],
    queryFn: async (): Promise<CatalogVehicle | null> => {
      const r = await api.erp.searchVehicles({
        body: { code: code!, page: 1, perPage: 1 },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o veículo no SGA'));
      if (r.body.estado !== 'lida') return null;
      /*
       * `erpCode`: o `code` da resposta é o da CASA desde 05/10/2026, e o que
       * o sinistro guarda é o do SGA. Comparar com o errado devolvia `null` com
       * a busca tendo achado a linha — a ficha dizia "o espelho não tem este
       * código" sobre um veículo que está no cadastro.
       */
      return r.body.items.find((v) => v.erpCode === code) ?? null;
    },
    enabled: code !== null && code !== '' && canSearchVehicles && tenantId !== undefined,
  });
}

export function useClaimMutations() {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: [...ROOT] });

  const create = useMutation({
    mutationFn: async (body: CreateClaim): Promise<Claim> =>
      ok(
        await api.claims.createClaim({ body }),
        201,
        'Não foi possível abrir o sinistro.',
      ),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: UpdateClaim }): Promise<Claim> =>
      ok(
        await api.claims.updateClaim({ params: { id }, body }),
        200,
        body.archived === true
          ? 'Não foi possível arquivar o sinistro.'
          : body.archived === false
            ? 'Não foi possível reativar o sinistro.'
            : 'Não foi possível corrigir o sinistro.',
      ),
    onSuccess: refresh,
  });

  return { create, update };
}

export type { CatalogVehicle, Claim, ClaimSummary };
